import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import { Lock, Mail, LogIn, ShieldCheck, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';

const RESEND_COOLDOWN = 60;

export const AdminLoginPage = () => {
  const [step, setStep] = useState(1); // 1 = credentials, 2 = 2FA code
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [tempToken, setTempToken] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);
  const inputRefs = useRef([]);

  const { loginStep1, loginStep2, resend2fa } = useAdminAuth();
  const navigate = useNavigate();

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Auto-focus first input when step 2 opens
  useEffect(() => {
    if (step === 2 && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [step]);

  const handleStep1 = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const result = await loginStep1(email, password);
      if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setEmailHint(result.email_hint);
        setStep(2);
        setResendCooldown(RESEND_COOLDOWN);
        toast.success('Verification code sent to your email');
      } else if (result.access_token) {
        // Trusted device — skip 2FA
        toast.success('Welcome back! (Trusted device)');
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...code];
      digits.forEach((d, i) => {
        if (index + i < 6) newCode[index + i] = d;
      });
      setCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();

      // Auto-submit if all 6 filled
      if (newCode.every(d => d !== '')) {
        handleVerify(newCode.join(''));
      }
      return;
    }

    if (!/^\d?$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit if all 6 filled
    if (newCode.every(d => d !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (fullCode) => {
    const codeStr = fullCode || code.join('');
    if (codeStr.length !== 6) {
      toast.error('Please enter the full 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await loginStep2(tempToken, codeStr, trustDevice);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      const result = await resend2fa(tempToken);
      toast.success('New code sent!');
      setResendCooldown(RESEND_COOLDOWN);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    }
  };

  const handleBackToLogin = () => {
    setStep(1);
    setTempToken('');
    setCode(['', '', '', '', '', '']);
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="font-['Bodoni_Moda'] text-3xl font-bold text-white mb-2">COUNTRY BO</h1>
          <p className="text-[#888] text-sm">Admin Dashboard</p>
        </div>

        {/* Step 1: Credentials */}
        {step === 1 && (
          <div className="bg-[#2a2a2a] rounded-xl p-8 border border-[#3a3a3a]" data-testid="login-step-1">
            <form onSubmit={handleStep1} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#ccc] text-sm font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="management@countrybofoods.com"
                    className="pl-10 bg-[#1a1a1a] border-[#3a3a3a] text-white placeholder:text-[#555] focus:border-[#D4540A]"
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#ccc] text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="********"
                    className="pl-10 bg-[#1a1a1a] border-[#3a3a3a] text-white placeholder:text-[#555] focus:border-[#D4540A]"
                    data-testid="admin-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4540A] hover:bg-[#B84709] text-white py-6 font-semibold"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-5 h-5" /> Sign In
                  </span>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* Step 2: 2FA Code */}
        {step === 2 && (
          <div className="bg-[#2a2a2a] rounded-xl p-8 border border-[#3a3a3a]" data-testid="login-step-2">
            {/* Back button */}
            <button
              onClick={handleBackToLogin}
              className="flex items-center gap-1 text-[#888] hover:text-white text-sm mb-6 transition-colors"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>

            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-[#D4540A]/10 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-[#D4540A]" />
              </div>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-white text-lg font-semibold mb-2">Verify your identity</h2>
              <p className="text-[#888] text-sm">
                We sent a verification code to<br />
                <span className="text-white font-mono">{emailHint}</span>
              </p>
            </div>

            {/* Code inputs */}
            <div className="flex justify-center gap-2 mb-6" data-testid="code-inputs">
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-bold font-mono bg-[#1a1a1a] border-2 border-[#3a3a3a] rounded-lg text-white focus:border-[#D4540A] focus:outline-none transition-colors"
                  data-testid={`code-input-${i}`}
                />
              ))}
            </div>

            {/* Trust this device */}
            <label className="flex items-center gap-3 cursor-pointer py-2" data-testid="trust-device-toggle">
              <button
                type="button"
                onClick={() => setTrustDevice(!trustDevice)}
                className={`w-10 h-6 rounded-full transition-colors flex items-center ${trustDevice ? 'bg-[#D4540A] justify-end' : 'bg-[#444] justify-start'}`}
              >
                <div className="w-4 h-4 bg-white rounded-full mx-1" />
              </button>
              <div>
                <p className="text-white text-sm">Trust this device for 30 days</p>
                <p className="text-[#666] text-xs">Skip verification on future logins</p>
              </div>
            </label>

            {/* Verify button */}
            <Button
              onClick={() => handleVerify()}
              disabled={loading || code.some(d => !d)}
              className="w-full bg-[#D4540A] hover:bg-[#B84709] text-white py-6 font-semibold mb-4"
              data-testid="verify-code-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5" /> Verify & Sign In
                </span>
              )}
            </Button>

            {/* Resend */}
            <div className="text-center">
              {resendCooldown > 0 ? (
                <p className="text-[#666] text-sm">
                  Resend code in <span className="text-[#D4540A] font-mono font-bold">{resendCooldown}s</span>
                </p>
              ) : (
                <button
                  onClick={handleResend}
                  className="text-[#D4540A] hover:text-[#B84709] text-sm font-medium flex items-center gap-1 mx-auto transition-colors"
                  data-testid="resend-code-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Resend code
                </button>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-[#555] text-xs mt-6">
          Protected admin area. Unauthorized access is prohibited.
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
